/**
 * ConfigurarBeneficioV2 — Employee contributions management page (V2 redesign).
 *
 * Sections:
 * 1. Hero: Next contribution countdown with CTA
 * 2. Key metrics: Company contribution, your contribution, annual limit
 * 3. Plan conditions summary
 * 4. Contribution history (tabbed: recurrentes vs bonus)
 * 5. Transfer requests status
 * 6. Plan management CTAs (transfer, register external)
 */
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { PageHeader } from '../../../components/ui/page-header';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '../../../components/ui/table';
import ResponsiveTooltip from '../../../components/ui/responsive-tooltip';
import {
	Building2,
	User,
	Calendar,
	Settings2,
	Star,
	TrendingUp,
	Clock,
	CheckCircle2,
	AlertCircle,
	Gift,
	Info,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ArrowRightLeft,
	PiggyBank,
	Loader2,
	Calculator
} from 'lucide-react';
import { motion } from 'framer-motion';

import { ArcaContext } from '../../../context/ArcaContext.context';
import useWithdrawalRequests from '../../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../../hooks/useRescuesEnabled';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import getCalculatorsUserAportations, {
	PaginatedMonthContributions
} from '../../../api/calculators/getCalculatorsUserAportations';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { CompanyWithEppConfigI } from '../../../api/companies/interfaces/CompanyInfo';
import getCurrentUser from '../../../api/users/getCurrentUser';
import getAdhesionDetails from '../../../api/users/getAdhesionDetails';
import { getPlanProductCodes } from '../../../utils/getPlanProductCodes';
import {
	calculateTotalSaved,
	sumAdhesionDetailsField
} from '../../../utils/pensionPlanCalculations';
import getCompanyNextContributionInfo from '../../../api/companies/getCompanyNextContributionInfo';
import getIndividualPensionPlan from '../../../api/users/getIndividualPensionPlan';
import { IndividualPensionPlan } from '../../../api/users/interfaces/IndividualPensionPlan';
import {
	getAllBonuses,
	ExtraordinaryBonus,
	BonusStatusType
} from '../../../api/bonus/getBonusConfig';
import { formatArcaNumber, fmtNum } from '../../../utils/formatArcaNumbers';
import { EmployeeContributionTypeEnum } from '../../../types/enums';
import { resolveEmployerContribution } from '../../../utils/resolveEmployerContribution';
import { GenericPaginationOutput } from '../../../api/generic-dtos/genericPaginationOutput';
import { CompanyEppConfigI } from '../../../api/companies/interfaces/CompanyEppConfig';
import { CurrentUserI } from '../../../api/users/interfaces/Users';
import {
	classifyContribution,
	isRescate,
	sumRescates,
	ContributionCategoryEnum
} from '../../../utils/contributionClassification';
import { CONTRIBUTION_LIMITS } from '../../../utils/contributionLimits';
import ConfigureContributionWrapper from '../../../components/MiBeneficioV2/ConfigureContributionWrapper';
import PensionPlanTransferJourneyV2 from '../../../components/PensionPlanTransferJourneyV2';
import IndividualPensionPlanFlowV2 from '../../../components/MiBeneficioV2/IndividualPensionPlanFlowV2';
import RescueRequestDialogV2 from '../../../components/RescueRequestDialogV2';
import RescueTaxSimulatorV2 from '../../../components/SimulatorsV2/RescueTaxSimulatorV2';
import SolicitarRescatePorMotivoDialogV2 from '../../../components/SolicitarRescatePorMotivoDialogV2';
import {
	RESCUE_REASONS,
	reasonStopsContributions,
	RescueRequestStatus
} from '../../../utils/rescueDocsCatalog';
import { esExEmpleado } from '../../../utils/esExEmpleado';

// Status badge config
const STATUS_CONFIG = {
	CONFIRMED: {
		label: 'Confirmado',
		icon: CheckCircle2,
		className: 'bg-primary/10 text-primary border-primary/20'
	},
	PENDING: {
		label: 'Pendiente',
		icon: Clock,
		className: 'bg-amber-500/10 text-amber-600 border-amber-500/20'
	},
	FAILED: {
		label: 'Error',
		icon: AlertCircle,
		className: 'bg-destructive/10 text-destructive border-destructive/20'
	}
};

// Format month from "YYYY-MM" to Spanish
const formatMonth = (fecha: string): string => {
	const [year, month] = fecha.split('-');
	const months = [
		'Ene',
		'Feb',
		'Mar',
		'Abr',
		'May',
		'Jun',
		'Jul',
		'Ago',
		'Sep',
		'Oct',
		'Nov',
		'Dic'
	];
	return `${months[parseInt(month) - 1]} ${year}`;
};

// Format currency
const fmtEur = (val: number) => formatArcaNumber(val) + '€';

// Stagger animation
const stagger = {
	hidden: { opacity: 0, y: 12 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.06, duration: 0.35, ease: 'easeOut' as const }
	})
};

const ConfigurarBeneficioV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId, userId } = useContext(ArcaContext);

	// Loading state
	const [isLoading, setIsLoading] = useState(true);

	// Data state
	const [currentUser, setCurrentUser] = useState<CurrentUserI | null>(null);
	const [companyEppConfig, setCompanyEppConfig] = useState<CompanyEppConfigI | null>(null);
	const [contributions, setContributions] =
		useState<GenericPaginationOutput<PaginatedMonthContributions> | null>(null);
	const [bonuses, setBonuses] = useState<ExtraordinaryBonus[]>([]);
	const [nextContributionInfo, setNextContributionInfo] = useState<any>(null);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(5);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});

	// Modal state
	const [showConfigModal, setShowConfigModal] = useState(false);
	const [showTransferDialog, setShowTransferDialog] = useState(false);
	const [showExternalPlanFlow, setShowExternalPlanFlow] = useState(false);
	// Declaración de plan externo de ESTE año (el backend filtra por año): decide si el
	// botón invita a registrar o a editar. Se carga en su propio efecto y no dentro de
	// `fetchData`, que encadena sus llamadas: así no añade latencia a la carga principal.
	const [individualPlan, setIndividualPlan] = useState<IndividualPensionPlan | null>(null);
	const hasDeclaredExternalPlan = !!(individualPlan?.hasIndividual && individualPlan.active);

	const fetchIndividualPlan = useCallback(() => {
		if (!token) return;
		getIndividualPensionPlan(token)
			.then((res) => {
				// Solo se pisa el estado con una respuesta buena: un fallo pasajero no debe
				// hacer que el botón vuelva a "Registrar" después de haber declarado.
				if (res?.success) setIndividualPlan(res.data ?? null);
			})
			.catch(() => {});
	}, [token]);

	useEffect(() => {
		fetchIndividualPlan();
	}, [fetchIndividualPlan]);
	const [showRescueDialog, setShowRescueDialog] = useState(false);
	const [showCalculatorModal, setShowCalculatorModal] = useState(false);
	const [showSolicitarRescateModal, setShowSolicitarRescateModal] = useState(false);
	// Estado de rescate de ESTE plan, desde backend. El hook resuelve el plan
	// por modo y distingue "hay una solicitud viva" (badge) de "hay una
	// confirmada que corta aportaciones" (la vista pivota).
	const {
		userEppConfigId,
		openRequest,
		activeStoppingReason,
		refresh: refreshWithdrawals
	} = useWithdrawalRequests(token, 'EMPLOYMENT');
	// Puerta de entrada a rescates: cerrada por defecto y abierta caso a caso
	// mientras no sepamos por Caser quién tiene algo rescatable.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, companyId, userId);
	const openRescueReason = openRequest?.reason ?? null;
	const stoppingRescueReason = activeStoppingReason;
	const activeRescueStatus = openRequest ? 'pending' : null;

	// Años con aportaciones/rescates reales (no todo el rango alta→hoy) — null
	// mientras no se ha comprobado todavía, para no mostrar el selector vacío
	// un instante antes de tener la respuesta.
	const [yearsWithActivity, setYearsWithActivity] = useState<number[] | null>(null);
	const [planProductCodesForYears, setPlanProductCodesForYears] = useState<number[]>([]);
	const [vestedRights, setVestedRights] = useState(0);

	// UI state
	const [justSaved, setJustSaved] = useState(false);

	// Fetch all data
	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			// Fetch current user
			const userRes = await getCurrentUser(token);
			if (userRes.success) {
				setCurrentUser(userRes.data);
			}

			// Fetch company config
			if (companyId) {
				// Si falla (la empresa de un ex-empleado puede estar inactiva), los movimientos se
				// cargan igual (product#87).
				const companyRes = await getCompanyInfoEppConfig(token, companyId).catch(() => null);
				const company = companyRes?.data?.allCompanies?.find(
					(c: CompanyWithEppConfigI) => c.id === companyId
				);
				if (company?.companyEppConfig) {
					setCompanyEppConfig(company.companyEppConfig);
				}

				// Fetch next contribution info. Si falla (p. ej. la empresa de un ex-empleado ya
				// está inactiva), no se lleva por delante la carga de los movimientos (product#87).
				const nextContribRes = await getCompanyNextContributionInfo(token).catch(() => null);
				if (nextContribRes?.success) {
					setNextContributionInfo(nextContribRes.data);
				}
			}

			// Adhesión del plan de empleo para scopear las aportaciones por plan
			// (multi-cuenta). Si falla/viene vacía → planCodes = [] → backend legacy.
			const adhesionRes = await getAdhesionDetails(token, 'EMPLOYMENT').catch(() => null);
			const planCodes = getPlanProductCodes(adhesionRes?.data ?? null);
			setPlanProductCodesForYears(planCodes);
			// Saldo real del plan (céntimos → euros). Es el tope del slider de
			// importe en los modales de rescate: inventarlo sería pedir un importe
			// sobre un máximo que no existe.
			setVestedRights(sumAdhesionDetailsField(adhesionRes?.data ?? [], 'vestedRights') / 100);

			// Fetch contributions
			const contribRes = await getCalculatorsUserAportations(token, {
				page: currentPage,
				limit: pageSize,
				year: selectedYear,
				productCodes: planCodes
			});
			if (contribRes) {
				setContributions(contribRes);
			}

			// Fetch bonuses
			if (userId) {
				const bonusRes = await getAllBonuses(token);
				if (bonusRes.success && bonusRes.data) {
					setBonuses(bonusRes.data.bonuses || []);
				}
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setIsLoading(false);
		}
	}, [token, companyId, userId, currentPage, pageSize, selectedYear]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// El selector de años solo debe mostrar años con actividad real, no todo
	// el rango desde el alta — un usuario con 11 años de antigüedad pero
	// aportaciones sembradas solo en el año actual no debería ver 11 pestañas
	// vacías. Comprobamos año a año (en paralelo) si hay algo, una sola vez
	// por usuario (no cada vez que cambia `selectedYear`).
	useEffect(() => {
		if (!currentUser?.userEppConfig?.createdAt) return;
		let cancelled = false;
		const now = new Date().getFullYear();
		const enrollmentYear = new Date(currentUser.userEppConfig.createdAt).getFullYear();
		const candidateYears: number[] = [];
		for (let y = now; y >= enrollmentYear; y--) {
			candidateYears.push(y);
		}
		Promise.all(
			candidateYears.map((year) =>
				getCalculatorsUserAportations(token, {
					page: 1,
					limit: 1,
					year,
					productCodes: planProductCodesForYears
				}).catch(() => null)
			)
		).then((results) => {
			if (cancelled) return;
			const active = candidateYears.filter((_, i) => (results[i]?.totalItems || 0) > 0);
			setYearsWithActivity(active.length > 0 ? active : [now]);
		});
		return () => {
			cancelled = true;
		};
	}, [token, currentUser?.userEppConfig?.createdAt, planProductCodesForYears]);

	// Calculate metrics (per-employee, not company aggregates)
	const companyContribution = resolveEmployerContribution({
		calculatedEmployerContribution: currentUser?.calculatedEmployerContribution,
		companyEppConfig,
		employeeCategory:
			currentUser?.user?.employeeCategory || currentUser?.userEppConfig?.data?.employeeCategory,
		grossAnnualSalary: currentUser?.user?.grossAnnualSalary,
		employmentStartDate: currentUser?.user?.employmentStartDate
	});
	const rawPensionContribution = Number(currentUser?.userEppConfig?.data?.pensionContribution || 0);
	const employeeContributionType = currentUser?.userEppConfig?.data?.employeeContributionType as
		| string
		| undefined;
	const userSalary = currentUser?.user?.grossAnnualSalary || 0;
	const userContribution =
		employeeContributionType === EmployeeContributionTypeEnum.PERCENTAGE && userSalary > 0
			? ((rawPensionContribution / 100) * userSalary) / 12
			: rawPensionContribution;
	const nextTotal = companyContribution + userContribution;

	// Days remaining — compute from nextContributionDate or contributionDay
	const daysRemaining = (() => {
		if (nextContributionInfo?.nextContributionDate) {
			const target = new Date(nextContributionInfo.nextContributionDate);
			const diff = Math.ceil((target.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
			return Math.max(diff, 0);
		}
		const now = new Date();
		const day = companyEppConfig?.contributionDay || 15;
		const nextDate =
			now.getDate() <= day
				? new Date(now.getFullYear(), now.getMonth(), day)
				: new Date(now.getFullYear(), now.getMonth() + 1, day);
		return Math.ceil((nextDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
	})();
	const daysProgress = ((30 - daysRemaining) / 30) * 100;

	// Next contribution date
	const nextDate = nextContributionInfo?.nextContributionDate
		? new Date(nextContributionInfo.nextContributionDate).toLocaleDateString('es-ES', {
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			})
		: '—';

	// Annual totals from contributions
	const yearContributions = contributions?.items || [];
	// La columna "Has rescatado" solo se muestra si hay al menos un RESCATE real
	// (CANCELLMENT_PARTICIPANT_SAVINGS). Las correcciones NO cuentan como rescate.
	const hasRescates = yearContributions.some((monthData) =>
		monthData.contributions.some((c) => isRescate(c.typeContribution))
	);
	const companyAnnual = yearContributions.reduce((sum, monthData) => {
		const monthTotal = monthData.contributions
			.filter((c) => classifyContribution(c.typeContribution) === ContributionCategoryEnum.COMPANY)
			.reduce((s, c) => s + (c.contributionAmount || 0), 0);
		return sum + monthTotal;
	}, 0);
	const userAnnual = yearContributions.reduce((sum, monthData) => {
		const monthTotal = monthData.contributions
			.filter(
				(c) => classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT
			)
			.reduce((s, c) => s + (c.contributionAmount || 0), 0);
		return sum + monthTotal;
	}, 0);
	const totalAnnual = companyAnnual + userAnnual;
	// Cap legal global anual según tipo de usuario:
	//   - Individual: 1.500€
	//   - Autónomo (puro o societario): 5.750€ (4.250 plan + 1.500 individual)
	//   - Empleado: 10.000€ (8.500 plan + 1.500 individual)
	const maxAnnual = currentUser?.user?.isIndividual
		? CONTRIBUTION_LIMITS.GLOBAL_INDIVIDUAL
		: currentUser?.user?.isFreelance
			? CONTRIBUTION_LIMITS.GLOBAL_FREELANCE
			: CONTRIBUTION_LIMITS.GLOBAL_EMPLOYEE;
	const planProgress = Math.min((totalAnnual / maxAnnual) * 100, 100);
	const remaining = Math.max(maxAnnual - totalAnnual, 0);

	// UX SCAFFOLD (Notion "Definimos MVP rescates") — hardcodeado para validar
	// cómo se le daría protagonismo al rescate a un usuario ya BENEFICIARIO/
	// jubilado. Sin persistencia real: no existe hoy ningún campo de "estado
	// del partícipe" (ACTIVO/BENEFICIARIO) en nuestro backend.
	//
	// Generalización: cualquier ACTIVO que rescate por un motivo que implica
	// dejar de aportar (ver `stopsContributions`) pasa a esta misma vista.
	// Sale del backend y de nada más: ni email ni localStorage. Antes había un
	// override por email para los usuarios de QA, que pisaba el dato real y
	// hacía imposible probar el flujo de verdad con ellos.
	const isRetiredBeneficiary = rescuesEnabled && reasonStopsContributions(stoppingRescueReason);
	// product#87: la empresa le dio de baja. Ve su historial, pero ni la próxima aportación,
	// ni el bonus, ni lo que le aporta la empresa. Si además rescata, manda la vista de rescate,
	// y aun así pierde lo que esto oculta fuera de ella («Traspasar plan», el plan externo): no
	// tienen sentido para quien rescata (revisión total, Marisa 01/11).
	const vistaExEmpleado = esExEmpleado(currentUser);
	// Las tarjetas de arriba llenan la fila (Marisa, 01/11). Al ex-empleado le quedan «Tú has
	// aportado» y, con rescates, la de rescate; el empleado en activo sigue como hoy.
	const rejillaMetricas = !vistaExEmpleado
		? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'
		: rescuesEnabled
			? 'grid grid-cols-1 md:grid-cols-2 gap-4'
			: 'grid grid-cols-1 gap-4';
	// Derechos consolidados totales del caso de referencia (dato real, ver
	// Notion "Definimos MVP rescates" — NIF anonimizado en este comentario a
	// propósito, confirmado en wm.caser.es 19/07/2026).
	// isRicardoActive se comprueba ANTES que isRetiredBeneficiary: si Ricardo
	// rescata por un motivo que corta aportaciones, isRetiredBeneficiary pasa
	// a true para él también, pero su importe sigue siendo el suyo (mismo
	// total que en MiBeneficioV2.tsx), no el de María (bug real, corregido
	// en revisión).
	// Saldo real del plan. Antes eran dos importes fijos del scaffold, que con
	// el estado saliendo del backend enseñaban a cualquiera el saldo de otra
	// persona.
	const availableToRescue = vestedRights;

	// UX SCAFFOLD (Notion "Definimos MVP rescates") — pensado para CUALQUIER
	// empleado ACTIVO (no jubilado/beneficiario, no autónomo) con un plan de
	// empleo: esta card (y todo el bloque que la contiene) solo se renderiza
	// bajo !isRetiredBeneficiary, así que quien la ve por definición no ha
	// cortado aportaciones — "Rescatado (año)" ya no aplica aquí, la card es
	// siempre informativa. El importe "rescatable por antigüedad" no existe
	// como campo real en nuestro backend para nadie (ni siquiera Ricardo,
	// cuyo importe de demo, 191,701859 participaciones / 1.724,00€, ya no se
	// muestra) — de ahí que sea informativa en vez de afirmar un importe, con
	// el mismo CTA "Solicitar rescate" para todos.
	// Motivo real por el que se ha pivotado a esta vista — María no pasa por
	// el modal de motivo (su caso es histórico/hardcodeado), así que cae al
	// fallback "Jubilación"; cualquier otro usuario ve su motivo real.
	const activeRescueReasonLabel = (() => {
		// Esta etiqueta vive en la cabecera de la vista pivotada, así que describe
		// el motivo que la pivotó: el de la solicitud confirmada.
		const found = RESCUE_REASONS.find((r) => r.value === stoppingRescueReason);
		if (found) return translate(found.labelKey);
		return translate('rescue.configurarBeneficio.reasonFallbackGeneric');
	})();

	// Available years — solo los que tienen actividad real (ver efecto de
	// arriba). Mientras se comprueba (yearsWithActivity === null), fallback al
	// rango completo alta→hoy para no dejar el selector vacío un instante.
	const availableYears = useMemo(() => {
		if (yearsWithActivity) return yearsWithActivity;
		const now = new Date().getFullYear();
		const enrollmentYear = currentUser?.userEppConfig?.createdAt
			? new Date(currentUser.userEppConfig.createdAt).getFullYear()
			: now;
		const years = [];
		for (let y = now; y >= enrollmentYear; y--) {
			years.push(y);
		}
		return years.length > 0 ? years : [now];
	}, [currentUser, yearsWithActivity]);

	// Pagination
	const totalPages = contributions?.totalPages || 1;

	// Toggle row expansion
	const toggleRow = (monthKey: string) => {
		setExpandedRows((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
	};

	// Handle year change
	const handleYearChange = (year: number) => {
		setSelectedYear(year);
		setCurrentPage(1);
		setExpandedRows({});
	};

	// Handle contribution save (modal close handled by wrapper)
	const handleSaveContribution = () => {
		setJustSaved(true);
		setTimeout(() => setJustSaved(false), 3000);
		fetchData();
	};

	// Pending and decided bonuses
	const pendingBonuses = bonuses.filter((b) => b.status === BonusStatusType.PENDING);
	const decidedBonuses = bonuses.filter((b) => b.status === BonusStatusType.DECIDED);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="max-w-6xl mx-auto space-y-8 pb-8">
			{/* 🔴 Cabecera nueva. Esta pantalla no tenía título propio: sus dos `h1` —«Tu ahorro
			    en el plan» y «Próxima aportación»— son títulos de la tarjeta del hero, y encima
			    eran DOS excluyentes. Se llamaba «Movimientos» en el menú y en ninguna parte de la
			    página. Los dos bajan a `h2`, que es lo que siempre fueron.
			    El título sale de la MISMA clave que la etiqueta del menú. El subtítulo es nuevo y
			    está escrito para servir a los dos casos —quien sigue aportando y quien ya solo
			    consulta—, porque la pantalla es la misma (aprobado por Marisa, 29/09/26).
			    `width="full"` porque el ancho lo pone el contenedor (`max-w-6xl`). */}
			<PageHeader
				width="full"
				title={translate('rescue.movementsTab.label')}
				subtitle={translate('contributions.screenSubtitle')}
			/>

			{/* 1. HERO: NEXT CONTRIBUTION — para un beneficiario/jubilado ya no hay
			    próxima aportación (ya no puede aportar): el hero pasa a estar
			    centrado en el rescate, que es la única acción que le queda. */}
			{isRetiredBeneficiary ? (
				<div className="individual-v2">
					<motion.div
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4 }}
					>
						<Card className="border-2 border-primary/30 shadow-xs overflow-hidden">
							<CardContent className="p-0">
								<div className="grid grid-cols-1 lg:grid-cols-3">
									<div className="p-6 lg:col-span-2">
										<div className="flex items-center gap-2 mb-3">
											<PiggyBank className="w-5 h-5 text-primary" />
											<h2 className="text-lg font-semibold font-heading">
												{translate('rescue.configurarBeneficio.heroTitle')}
											</h2>
											<Badge className="bg-primary/10 text-primary border-primary/20">
												{translate('rescue.configurarBeneficio.beneficiaryBadgePrefix')}{' '}
												{activeRescueReasonLabel}
											</Badge>
										</div>
										<div className="flex items-baseline gap-3 mb-2">
											<p className="text-4xl font-bold font-heading text-foreground tracking-tight">
												{fmtEur(availableToRescue)}
											</p>
										</div>
										<p className="text-sm text-muted-foreground max-w-md">
											{translate('rescue.configurarBeneficio.heroSubtitle')}
										</p>
									</div>
									<div className="p-6 bg-primary/5 flex flex-col justify-center gap-3 border-t lg:border-t-0 lg:border-l border-border">
										<Button
											onClick={() => setShowRescueDialog(true)}
											className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
										>
											<PiggyBank className="w-4 h-4" />
											{translate('rescue.configurarBeneficio.requestButton')}
										</Button>
										<Button
											variant="outline"
											onClick={() => setShowCalculatorModal(true)}
											className="w-full border-primary text-primary hover:bg-primary/5 gap-2"
										>
											<Calculator className="w-4 h-4" />
											{translate('rescue.configurarBeneficio.calcButton')}
										</Button>
									</div>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</div>
			) : vistaExEmpleado ? null : (
				<motion.div
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
				>
					<Card
						className={`border-2 shadow-xs overflow-hidden transition-colors duration-500 ${justSaved ? 'border-primary/50 bg-primary/5' : 'border-primary/20'}`}
					>
						<CardContent className="p-0">
							<div className="grid grid-cols-1 lg:grid-cols-3">
								{/* Left: Next contribution amount + date */}
								<div className="p-6 lg:col-span-2">
									<div className="flex items-center gap-2 mb-3">
										<Calendar className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold">
											{translate('contributions.nextContribution')}
										</h2>
										{justSaved && (
											<Badge className="bg-primary/10 text-primary border-primary/20 gap-1 animate-in fade-in slide-in-from-left-2">
												<CheckCircle2 className="w-3 h-3" />
												{translate('common.updated')}
											</Badge>
										)}
									</div>

									{/* Big total amount */}
									<div className="flex items-baseline gap-3 mb-2">
										<p className="text-4xl font-bold text-foreground tracking-tight">
											{fmtEur(nextTotal)}
										</p>
										<span className="text-sm text-muted-foreground">
											{translate('contributions.on')} {nextDate}
										</span>
									</div>

									{/* Breakdown */}
									<div className="flex items-center gap-4 mb-4">
										<div className="flex items-center gap-1.5 text-sm">
											<Building2 className="w-4 h-4 text-primary" />
											<span className="text-muted-foreground">
												{translate('contributions.company')}:
											</span>
											<span className="font-semibold">{fmtEur(companyContribution)}</span>
										</div>
										<span className="text-muted-foreground">+</span>
										<div className="flex items-center gap-1.5 text-sm">
											<User className="w-4 h-4 text-secondary" />
											<span className="text-muted-foreground">
												{translate('contributions.you')}:
											</span>
											<span className="font-semibold text-secondary">
												{employeeContributionType === EmployeeContributionTypeEnum.PERCENTAGE &&
												rawPensionContribution > 0
													? `${fmtNum(rawPensionContribution, 1)}% (${fmtEur(userContribution)})`
													: fmtEur(userContribution)}
											</span>
										</div>
									</div>

									<div className="flex items-center gap-3">
										<Progress value={daysProgress} className="h-2 flex-1" />
										<Badge
											variant="outline"
											className="text-primary border-primary/30 font-semibold"
										>
											{daysRemaining} {translate('contributions.days')}
										</Badge>
									</div>
									{daysRemaining <= 4 && daysRemaining > 0 && (
										<p className="text-xs text-amber-600 font-medium mt-2">
											{translate('contributions.deadlineWarning')}
										</p>
									)}
								</div>

								{/* Right: CTA */}
								<div className="p-6 bg-primary/5 flex flex-col justify-center gap-3 border-t lg:border-t-0 lg:border-l border-border">
									<Button
										onClick={() => setShowConfigModal(true)}
										className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full gap-2"
									>
										<Settings2 className="w-4 h-4" />
										{translate('contributions.configureMyContribution')}
									</Button>
									<Button
										variant="outline"
										className="w-full rounded-full gap-2 border-secondary text-secondary hover:bg-secondary/5"
										disabled={pendingBonuses.length === 0}
									>
										<Star className="w-4 h-4" />
										{pendingBonuses.length > 0
											? `${translate('contributions.decideBonus')} (${pendingBonuses.length})`
											: translate('contributions.decideBonus')}
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.div>
			)}

			{/* 2. KEY METRICS — para beneficiaria/jubilada, ya está el importe
			    rescatable destacado en el hero de arriba; repetirlo aquí (y las
			    aportaciones de empresa/tuyas, que ya no aplican) es redundante. */}
			{!isRetiredBeneficiary && (
				<div className={rejillaMetricas}>
					{/* Company contributed — el ex-empleado ya no recibe nada de la empresa. */}
					{!vistaExEmpleado && (
						<motion.div custom={0} variants={stagger} initial="hidden" animate="visible">
							<Card className="border-none shadow-xs hover:shadow-md transition-shadow h-full">
								<CardContent className="p-5">
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<Building2 className="w-5 h-5 text-primary" />
										</div>
										<ResponsiveTooltip
											content={translate('contributions.companyAnnualTip')}
											side="top"
										/>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('contributions.companyContributed')} ({selectedYear})
									</p>
									<p className="text-2xl font-bold text-primary tracking-tight">
										{fmtEur(companyAnnual)}
									</p>
									<p className="text-xs text-muted-foreground mt-1">
										{fmtEur(companyContribution)}/{translate('contributions.month')}
									</p>
								</CardContent>
							</Card>
						</motion.div>
					)}

					{/* You contributed */}
					<motion.div custom={1} variants={stagger} initial="hidden" animate="visible">
						<Card className="border-none shadow-xs hover:shadow-md transition-shadow h-full">
							<CardContent className="p-5">
								<div className="flex items-start justify-between mb-3">
									<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-secondary/10">
										<User className="w-5 h-5 text-secondary" />
									</div>
									<ResponsiveTooltip
										content={translate('contributions.yourAnnualTip')}
										side="top"
									/>
								</div>
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
									{translate('contributions.youContributed')} ({selectedYear})
								</p>
								<p className="text-2xl font-bold text-secondary tracking-tight">
									{fmtEur(userAnnual)}
								</p>
								{/* Ex-empleado: el total sí, pero ya no tiene aportación en curso (Marisa, 01/11). */}
								{!vistaExEmpleado && (
									<p className="text-xs text-muted-foreground mt-1">
										{fmtEur(userContribution)}/{translate('contributions.monthCurrent')}
									</p>
								)}
							</CardContent>
						</Card>
					</motion.div>

					{/* Annual limit — no al ex-empleado: le invitaría a aportar lo que no puede (Marisa, 01/11). */}
					{!vistaExEmpleado && (
						<motion.div custom={2} variants={stagger} initial="hidden" animate="visible">
							<Card className="border-none shadow-xs hover:shadow-md transition-shadow h-full">
								<CardContent className="p-5">
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<TrendingUp className="w-5 h-5 text-primary" />
										</div>
										<ResponsiveTooltip
											content={translate('contributions.annualLimitTip')}
											side="top"
										/>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('contributions.annualPlanLimit')}
									</p>
									<p className="text-2xl font-bold text-primary tracking-tight">
										{fmtEur(totalAnnual)}{' '}
										<span className="text-base font-normal text-muted-foreground">
											/ {fmtEur(maxAnnual)}
										</span>
									</p>
									<Progress value={planProgress} className="h-2 mt-3 mb-2" />
									<p className="text-xs text-muted-foreground">
										{translate('contributions.remaining')}{' '}
										<span className="font-semibold text-primary">{fmtEur(remaining)}</span>{' '}
										{translate('contributions.availableThisYear')}
									</p>
								</CardContent>
							</Card>
						</motion.div>
					)}

					{rescuesEnabled && (
						<motion.div custom={3} variants={stagger} initial="hidden" animate="visible">
							<Card className="border-none shadow-xs hover:shadow-md transition-shadow h-full">
								<CardContent className="p-5">
									{/* Card siempre informativa para todo activo (este bloque entero ya
								    está bajo !isRetiredBeneficiary): quien rescata por un motivo
								    que NO corta aportaciones mantiene la misma card/CTA que quien
								    nunca ha rescatado — "Rescatado (año)" ya no aplica aquí, un
								    activo por definición no ha cortado aportaciones. */}
									<div className="flex items-start justify-between mb-3">
										<div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
											<PiggyBank className="w-5 h-5 text-primary" />
										</div>
									</div>
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
										{translate('rescue.configurarBeneficio.infoLabel')}
									</p>
									<p className="text-lg font-bold text-primary tracking-tight">
										{translate('rescue.configurarBeneficio.infoValue')}
									</p>
									<p className="text-xs text-muted-foreground mt-1">
										{translate('rescue.configurarBeneficio.infoSub')}
									</p>
									{/* Este badge sólo aparece cuando hay una solicitud VIVA, y viva
								    quiere decir pendiente: una confirmada por un motivo que corta
								    aportaciones ya habría pivotado la vista, y las resueltas de
								    cualquier otra forma dejan el botón libre otra vez. */}
									{openRescueReason ? (
										<Badge
											variant="outline"
											className={`w-full mt-3 justify-center py-1.5 gap-1 ${STATUS_CONFIG.PENDING.className}`}
										>
											<Clock className="w-3.5 h-3.5" />
											{translate('rescue.configurarBeneficio.statusPending')}
										</Badge>
									) : (
										<Button
											size="sm"
											onClick={() => setShowSolicitarRescateModal(true)}
											className="w-full mt-3 gap-1.5"
										>
											{translate('rescue.configurarBeneficio.requestButtonSmall')}
										</Button>
									)}
								</CardContent>
							</Card>
						</motion.div>
					)}
				</div>
			)}
			{openRescueReason && (
				<p className="text-xs text-muted-foreground -mt-4">
					{translate('rescue.configurarBeneficio.reasonPrefix')}{' '}
					{(() => {
						const found = RESCUE_REASONS.find((r) => r.value === openRescueReason);
						return found ? translate(found.labelKey) : openRescueReason;
					})()}
				</p>
			)}

			{/* 3. PLAN CONDITIONS — no al ex-empleado: ya no le aplican (revisión total, Marisa 01/11). */}
			{!vistaExEmpleado && (
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-3">
								<Info className="w-4 h-4 text-muted-foreground" />
								<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
									{translate('contributions.planConditions')}
								</h3>
							</div>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
								<div>
									<p className="text-muted-foreground">{translate('contributions.periodicity')}</p>
									<p className="font-semibold">{translate('contributions.monthly')}</p>
								</div>
								<div>
									<p className="text-muted-foreground">
										{translate('contributions.employeeContribution')}
									</p>
									<p className="font-semibold">
										{companyEppConfig?.employeeContributionRequired
											? translate('contributions.mandatory')
											: translate('contributions.voluntary')}
									</p>
								</div>
								<div>
									<p className="text-muted-foreground">
										{translate('contributions.investmentType')}
									</p>
									<p className="font-semibold">{translate('contributions.indexedEquity')}</p>
								</div>
								<div>
									<p className="text-muted-foreground">{translate('contributions.memberSince')}</p>
									<p className="font-semibold">
										{currentUser?.userEppConfig?.createdAt
											? new Date(currentUser.userEppConfig.createdAt).toLocaleDateString('es-ES', {
													month: 'short',
													year: 'numeric'
												})
											: '—'}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.div>
			)}

			{/* 4. CONTRIBUTION HISTORY */}
			<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
				<Tabs defaultValue="recurrentes" className="w-full">
					<div className="flex items-center justify-between mb-4 flex-wrap gap-3">
						<div className="flex items-center gap-3">
							<h2 className="text-lg font-semibold">
								{/* UX SCAFFOLD — para beneficiaria/jubilada ya no son solo
								    "aportaciones" (ya no aporta): es historial de movimientos
								    (aportaciones pasadas + rescates). */}
								{isRetiredBeneficiary
									? translate('rescue.configurarBeneficio.historyTitle')
									: translate('contributions.history')}
							</h2>
							{availableYears.length >= 1 && (
								<div className="flex items-center gap-1 bg-muted/50 rounded-full p-0.5">
									{availableYears.map((y) => (
										<button
											key={y}
											onClick={() => handleYearChange(y)}
											className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
												y === selectedYear
													? 'bg-primary text-primary-foreground shadow-xs'
													: 'text-muted-foreground hover:text-foreground'
											}`}
										>
											{y}
										</button>
									))}
								</div>
							)}
						</div>
						{/* Bonus: ya no aplica para una beneficiaria/jubilada — no puede
						    seguir recibiendo ni decidir bonos. */}
						{!isRetiredBeneficiary && !vistaExEmpleado && (
							<TabsList className="bg-muted/50">
								<TabsTrigger
									value="recurrentes"
									className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs rounded-md text-sm"
								>
									{translate('contributions.recurring')}
								</TabsTrigger>
								<TabsTrigger
									value="bonus"
									className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs rounded-md text-sm"
								>
									<Gift className="w-3.5 h-3.5 mr-1.5" />
									Bonus
								</TabsTrigger>
							</TabsList>
						)}
					</div>

					<TabsContent value="recurrentes">
						<Card className="border-none shadow-xs overflow-hidden">
							<Table>
								<TableHeader>
									<TableRow className="bg-muted/30 hover:bg-muted/30">
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-8"></TableHead>
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											{translate('contributions.date')}
										</TableHead>
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											{translate('contributions.companyContributed')}
										</TableHead>
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											{translate('contributions.youContributed')}
										</TableHead>
										{hasRescates && (
											<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
												{translate('contributions.youRescued')}
											</TableHead>
										)}
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											Total
										</TableHead>
										<TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											{translate('contributions.status')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{yearContributions.length === 0 ? (
										<TableRow>
											<TableCell
												colSpan={hasRescates ? 7 : 6}
												className="text-center py-8 text-muted-foreground"
											>
												{translate('contributions.noContributions')}
											</TableCell>
										</TableRow>
									) : (
										yearContributions.map((monthData) => {
											const monthKey = monthData.month;
											const isExpanded = expandedRows[monthKey];
											const companyTotal = monthData.contributions
												.filter(
													(c) =>
														classifyContribution(c.typeContribution) ===
														ContributionCategoryEnum.COMPANY
												)
												.reduce((s, c) => s + (c.contributionAmount || 0), 0);
											const participantTotal = monthData.contributions
												.filter(
													(c) =>
														classifyContribution(c.typeContribution) ===
														ContributionCategoryEnum.PARTICIPANT
												)
												.reduce((s, c) => s + (c.contributionAmount || 0), 0);
											// Rescates del mes. En BBDD el importe puede venir positivo; lo mostramos
											// como salida (negativo) usando su valor absoluto.
											const rescateOut = Math.abs(sumRescates(monthData.contributions));
											// Excluye ruido de rebalanceo interno Caser (TRANSFER_INTERNAL_*, etc.) del detalle expandido
											const visibleContributions = monthData.contributions.filter(
												(c) => classifyContribution(c.typeContribution) !== null
											);
											const hasDetails = visibleContributions.length > 1;
											// El estado del mes refleja el peor movimiento visible del mes
											// (si algo sigue pendiente/con error, el mes entero se marca
											// así) en vez de dar por Confirmado todo por defecto.
											const status = visibleContributions.some(
												(c) => c.statusContribution === 'PENDING'
											)
												? STATUS_CONFIG.PENDING
												: visibleContributions.some((c) => c.statusContribution === 'ERROR')
													? STATUS_CONFIG.FAILED
													: STATUS_CONFIG.CONFIRMED;
											const StatusIcon = status.icon;

											return (
												<React.Fragment key={monthKey}>
													<TableRow
														className={`hover:bg-muted/10 ${hasDetails ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-muted/20' : ''}`}
														onClick={() => hasDetails && toggleRow(monthKey)}
													>
														<TableCell className="w-8 px-2">
															{hasDetails && (
																<ChevronDown
																	className={`w-4 h-4 text-primary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
																/>
															)}
														</TableCell>
														<TableCell className="font-semibold">{formatMonth(monthKey)}</TableCell>
														<TableCell>{companyTotal > 0 ? fmtEur(companyTotal) : '—'}</TableCell>
														<TableCell className="font-semibold text-secondary">
															{participantTotal > 0 ? fmtEur(participantTotal) : '—'}
														</TableCell>
														{hasRescates && (
															<TableCell className="font-semibold text-red-500">
																{rescateOut !== 0 ? fmtEur(-rescateOut) : '—'}
															</TableCell>
														)}
														<TableCell className="font-semibold">
															{fmtEur(companyTotal + participantTotal - rescateOut)}
														</TableCell>
														<TableCell>
															<Badge variant="outline" className={`gap-1 ${status.className}`}>
																<StatusIcon className="w-3 h-3" />
																{status.label}
															</Badge>
														</TableCell>
													</TableRow>
													{isExpanded &&
														visibleContributions.map((c) => (
															<TableRow key={c.id} className="bg-muted/10 text-xs">
																<TableCell />
																<TableCell className="text-muted-foreground">
																	{new Date(c.dateContribution).toLocaleDateString('es-ES', {
																		day: 'numeric',
																		month: 'short'
																	})}
																</TableCell>
																<TableCell className="text-muted-foreground">
																	{classifyContribution(c.typeContribution) ===
																	ContributionCategoryEnum.COMPANY
																		? fmtEur(c.contributionAmount)
																		: '—'}
																</TableCell>
																<TableCell className="text-muted-foreground">
																	{classifyContribution(c.typeContribution) ===
																	ContributionCategoryEnum.PARTICIPANT
																		? fmtEur(c.contributionAmount)
																		: '—'}
																</TableCell>
																{hasRescates && (
																	<TableCell className="text-red-500">
																		{isRescate(c.typeContribution)
																			? fmtEur(-Math.abs(c.contributionAmount))
																			: '—'}
																	</TableCell>
																)}
																<TableCell className="text-muted-foreground">
																	{c.liquidationValue
																		? `${formatArcaNumber(c.liquidationValue)}€/part`
																		: '—'}
																</TableCell>
																<TableCell className="text-muted-foreground">
																	{c.sharesBought
																		? `${formatArcaNumber(c.sharesBought)} part.`
																		: '—'}
																</TableCell>
															</TableRow>
														))}
												</React.Fragment>
											);
										})
									)}
								</TableBody>
							</Table>
						</Card>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="flex items-center justify-center gap-2 mt-4">
								<Button
									variant="outline"
									size="sm"
									className="rounded-full w-8 h-8 p-0"
									disabled={currentPage === 1}
									onClick={() => setCurrentPage((p) => p - 1)}
								>
									<ChevronLeft className="w-4 h-4" />
								</Button>
								{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
									<Button
										key={page}
										variant={page === currentPage ? 'default' : 'outline'}
										size="sm"
										className={`rounded-full w-8 h-8 p-0 ${
											page === currentPage
												? 'bg-primary hover:bg-primary/90 text-primary-foreground'
												: ''
										}`}
										onClick={() => setCurrentPage(page)}
									>
										{page}
									</Button>
								))}
								<Button
									variant="outline"
									size="sm"
									className="rounded-full w-8 h-8 p-0"
									disabled={currentPage === totalPages}
									onClick={() => setCurrentPage((p) => p + 1)}
								>
									<ChevronRight className="w-4 h-4" />
								</Button>
							</div>
						)}
					</TabsContent>

					{!vistaExEmpleado && (
						<TabsContent value="bonus">
							<Card className="border-none shadow-xs">
								<CardContent className="p-6">
									{pendingBonuses.length === 0 && decidedBonuses.length === 0 ? (
										<div className="text-center py-6">
											<Star className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
											<p className="text-muted-foreground mb-1">
												{translate('contributions.noBonuses')}
											</p>
											<p className="text-sm text-muted-foreground">
												{translate('contributions.noBonusesDescription')}
											</p>
										</div>
									) : (
										<div className="space-y-3">
											{pendingBonuses.map((b) => (
												<div
													key={b.id}
													className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50/50"
												>
													<div>
														<p className="font-medium text-sm">Bonus extraordinario</p>
														<p className="text-xs text-muted-foreground">
															{translate('contributions.upTo')} {fmtEur(b.amount || 0)}
														</p>
													</div>
													<Button size="sm" className="gap-1.5">
														<Gift className="w-3.5 h-3.5" />
														{translate('contributions.decide')}
													</Button>
												</div>
											))}
											{decidedBonuses.map((b) => (
												<div
													key={b.id}
													className="flex items-center justify-between p-4 rounded-lg border bg-muted/20"
												>
													<div>
														<p className="font-medium text-sm">Bonus extraordinario</p>
														<p className="text-xs text-muted-foreground">
															{translate('contributions.contributed')}{' '}
															{fmtEur(b.decisionAmount || 0)} {translate('contributions.of')}{' '}
															{fmtEur(b.amount || 0)}
														</p>
													</div>
													<Badge
														variant="outline"
														className="bg-primary/10 text-primary border-primary/20 gap-1"
													>
														<CheckCircle2 className="w-3 h-3" />
														{translate('contributions.decided')}
													</Badge>
												</div>
											))}
										</div>
									)}
								</CardContent>
							</Card>
						</TabsContent>
					)}
				</Tabs>
			</motion.div>

			{/* 5. PLAN MANAGEMENT */}
			{/* Ex-empleado: tampoco, como en Mi beneficio. Su plan está suspendido y el traspaso es
			    solo de entrada (Marisa, 01/11). */}
			{!vistaExEmpleado && (
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
					<Card className="border-none shadow-xs bg-linear-to-r from-secondary/5 to-primary/5 border border-secondary/20">
						<CardContent className="p-6">
							<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
								<div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
									<PiggyBank className="w-6 h-6 text-secondary" />
								</div>
								<div className="flex-1 min-w-0">
									<h3 className="text-base font-semibold mb-1">
										{translate('user.externalPlan.ctaTitle')}
									</h3>
									<p className="text-sm text-muted-foreground">
										{translate('user.planActions.subtitle')}
									</p>
								</div>
								{/* 🔴 `flex-wrap`: los dos botones llevan `whitespace-nowrap` (base de `Button`), así
								    que su ancho mínimo es el del texto entero y `flex-1` no puede
								    encogerlos por debajo. A 360 px —el ancho de la mayoría de los
								    Android— «Registrar plan externo» se salía 26 px y arrastraba la
								    página entera. A 390 (iPhone) cabía, por eso la red no lo vio: mide
								    a 390. Con `flex-wrap` se apilan cuando no caben. */}
								<div className="flex flex-wrap gap-2 w-full sm:w-auto">
									<Button
										onClick={() => setShowTransferDialog(true)}
										className="bg-secondary hover:bg-secondary/90 text-white gap-1.5 flex-1 sm:flex-initial"
									>
										<ArrowRightLeft className="w-4 h-4" />
										{translate('user.planActions.transferButton')}
									</Button>
									<Button
										onClick={() => setShowExternalPlanFlow(true)}
										variant="outline"
										className="border-secondary text-secondary hover:bg-secondary/5 gap-1.5 flex-1 sm:flex-initial"
									>
										{/* El flujo se abre en modo edición, así que llamarlo "Registrar"
										    mentiría sobre lo que va a pasar. */}
										{hasDeclaredExternalPlan
											? translate('user.planActions.editExternalButton')
											: translate('user.planActions.registerButton')}
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.div>
			)}

			{/* Configure Contribution Modal */}
			<ConfigureContributionWrapper
				isOpen={showConfigModal}
				onClose={() => setShowConfigModal(false)}
				onSaved={handleSaveContribution}
			/>

			{/* Transfer Plan Dialog */}
			<PensionPlanTransferJourneyV2
				isOpen={showTransferDialog}
				onClose={() => setShowTransferDialog(false)}
			/>

			{/* External Plan Registration Flow */}
			<IndividualPensionPlanFlowV2
				isOpen={showExternalPlanFlow}
				// Acción explícita del usuario: se abre para editar lo ya declarado.
				mode="edit"
				onClose={() => {
					setShowExternalPlanFlow(false);
					fetchData();
				}}
				onComplete={() => {
					setShowExternalPlanFlow(false);
					fetchData();
				}}
				token={token}
			/>
			<RescueRequestDialogV2
				userEppConfigId={userEppConfigId}
				token={token}
				onSubmitted={() => refreshWithdrawals()}
				isOpen={showRescueDialog}
				onClose={() => setShowRescueDialog(false)}
				availableAmount={availableToRescue}
				email={currentUser?.user?.email || ''}
				currentReason={openRescueReason}
				seniorityAmount={undefined}
			/>
			<Dialog open={showCalculatorModal} onOpenChange={setShowCalculatorModal}>
				<DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto thin-scrollbar">
					<div className="individual-v2 -m-6 p-6 rounded-lg">
						<DialogHeader>
							<DialogTitle className="font-heading">
								{translate('rescue.configurarBeneficio.calcDialogTitle')}
							</DialogTitle>
						</DialogHeader>
						<div className="mt-6">
							<RescueTaxSimulatorV2
								initialAvailable={availableToRescue}
								cardTitle={translate('rescue.configurarBeneficio.calcCardTitle')}
							/>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<SolicitarRescatePorMotivoDialogV2
				isOpen={showSolicitarRescateModal}
				onClose={() => setShowSolicitarRescateModal(false)}
				userEmail={currentUser?.user?.email || ''}
				availableAmount={availableToRescue}
				currentReason={openRescueReason}
				userEppConfigId={userEppConfigId}
				token={token}
				onSubmitted={() => refreshWithdrawals()}
			/>
		</div>
	);
};

export default ConfigurarBeneficioV2;
